import React, { useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Tooltip, Table, message, Typography, TablePaginationConfig } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ProductNameColumn, SoldOutColumn, MembershipColumn } from './column';
import { PRODUCT } from '../../../constants/define/translation';
import { MESSAGE_ERROR } from '../../../constants/message';
import { appStyles } from '../../../styles';
import { IndexColumn } from '../shared-column';
import { postSortProductsRepository, ProductOrders } from '../../../repository/mytm/shop-product/shopProductRepository';

const { Column } = Table;

interface ProductOrdersTableProps {
  records: ProductOrders[];
  loading?: boolean;
  className?: string;
  onRefresh: () => void;
  setIsOpenModal: (boolean: Boolean) => void;
  setDataRecord: Function;
  pagination?: TablePaginationConfig;
  countrySelected?: string;
}

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

export const ProductTable = (props: ProductOrdersTableProps) => {
  const { t } = useTranslation(PRODUCT);
  const [isLoading, setIsLoading] = useState(false);
  const { records, loading, className, onRefresh, pagination, countrySelected } = props;
  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsLoading(true);
      const dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);

      postSortProductsRepository({
        sortOrderList: dragRecordsList.map((record, index) => ({
          id: record.id,
          productId: record.product_id,
          sortOrder: index + 1,
        })),
        country: countrySelected,
      })
        .then(() => {
          setIsLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setIsLoading(false);
        });
    },
    [records, onRefresh],
  );

  const handleEdit = (record: ProductOrders) => {
    props.setIsOpenModal(true);
    props.setDataRecord(record);
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledProductTable
          dataSource={records}
          loading={loading || isLoading}
          className={className}
          rowKey="index"
          components={components}
          pagination={pagination}
          onRow={(recordDeep: any, index?: number): any => {
            return {
              index,
              moveRow,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
            width="60px"
          />
          <Column
            title={t('table.title.soldOut')}
            align="center"
            dataIndex="sold_out"
            key="soldOut"
            render={(value: any, record: ProductOrders) => <SoldOutColumn record={record} onRefresh={onRefresh} />}
            width="80px"
          />
          <Column
            title={t('table.title.membership')}
            align="center"
            dataIndex="participationLevel"
            key="participationLevel"
            render={(value: any, record: ProductOrders) => <MembershipColumn record={record} onRefresh={onRefresh} />}
            width="80px"
          />
          <Column
            title={t('table.title.maxQty')}
            align="center"
            dataIndex="maxQty"
            key="maxQty"
            render={(value: any, record: ProductOrders) => (
              <Typography.Text className="card-name-tiles">{value}</Typography.Text>
            )}
            width="80px"
          />
          <Column
            title={t('table.title.product')}
            align="left"
            dataIndex="productName"
            key="productName"
            render={(value: any, record: ProductOrders) => <ProductNameColumn product={record} />}
            width="50%"
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={100}
            render={(_, record: ProductOrders) => (
              <Tooltip title={t('table.tooltip.edit')}>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              </Tooltip>
            )}
          />
        </StyledProductTable>
      </DndProvider>
    </>
  );
};

const StyledProductTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
